import React, { useEffect, useMemo, useRef, useState } from "react";
import CustomFileUploader from "../../components/common/CustomFileUploader";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Button as MUIButton,
  Tooltip as MUITooltip,
  Avatar,
  Chip,
  Autocomplete,
} from "@mui/material";
import {
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiPlus,
  FiExternalLink,
} from "react-icons/fi";

import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";

import { Formik } from "formik";
import * as Yup from "yup";

import "./location.css";
import apis from "../../utils/apis";
import httpAction from "../../utils/httpAction";
import toast from "react-hot-toast";
import staticData from "../../utils/staticData";

export default function Location() {
  // Demo data (replace with API later)
  const [rows, setRows] = useState([]);
  const formikRef = useRef(null);
  const [q, setQ] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [urls, setUrls] = useState([]);
  const [branchDrop, setBranchDrop] = useState([]);

  const initialValues = {
    imageUrl: editing?.imageUrl || "", // Default to an empty string if undefined
    title: editing?.title || "", // Default to an empty string if undefined
    city: editing?.city || "",
  };

  const schema = Yup.object({
    title: Yup.string().trim().min(2, "Too short").required("Name is required"),
    imageUrl: Yup.string().required("picture is required"),
    city: Yup.string().required("City is required"),
  });

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;

    return rows.filter((r) => {
      return (
        String(r?._id).includes(needle) ||
        r.name.toLowerCase().includes(needle) ||
        r.mobile.toLowerCase().includes(needle) ||
        r.branch.toLowerCase().includes(needle)
      );
    });
  }, [rows, q]);

  const openAdd = () => {
    setEditing(null);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setUrls([{ url: row?.imageUrl }]);
    setEditing(row);
    setShowModal(true);
  };

  const removeRow = async (id) => {
    const data = {
      url: apis().deletelocation + "?id=" + id,
      method: "DELETE",
    };

    const result = await httpAction(data);
    if (result?.status) {
      toast.success(result?.message);
      setRows((prev) => prev.filter((r) => r._id !== id));
    }
  };

  const saveUser = async (values) => {
    const data = {
      url: editing?.title
        ? apis().updatelocation + "?id=" + editing?._id
        : apis().addLocation,
      method: editing?.title ? "PUT" : "POST",
      body: values,
    };

    const result = await httpAction(data);

    if (result?.status) {
      toast.success(result?.message);
      setShowModal(false);

      if (editing?.title) {
        const findedIndex = rows.findIndex(
          (item) => item?._id === editing?._id
        );

        const userCopy = [...rows];
        userCopy.splice(findedIndex, 1, result?.location);
        setRows(userCopy);
      } else {
        setRows((prev) => [...prev, result?.location]);
      }
      setEditing(null);
      setUrls([]);
    }
  };

  const getUsers = async () => {
    const data = {
      url: apis().getLocationList,
    };

    const result = await httpAction(data);
    if (result?.status) {
      setRows(result?.list);
    }
  };

  const branchList = async () => {
    const data = {
      url: apis().branchList,
    };

    const result = await httpAction(data);
    if (result?.status) {
      setBranchDrop(result?.list);
    }
  };

  useEffect(() => {
    getUsers();
    branchList();
  }, []);

  const handleFileChange = (file, formik) => {
    const url = file[0] || "";
    formik.setFieldValue("imageUrl", url?.url);
  };

  return (
    <Box className="usersPage">
      <Box className="usersHeader">
        <Box>
          <Typography className="usersTitle">Locations</Typography>
          <Typography className="usersSub">
            Create and manage Locations here.
          </Typography>
        </Box>

        <MUIButton
          variant="contained"
          className="usersAddBtn"
          startIcon={<FiPlus />}
          onClick={openAdd}
        >
          Add New location
        </MUIButton>
      </Box>

      <Paper className="usersCard" elevation={0}>
        <Box className="usersToolbar">
          <TextField
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, mobile, branch, id..."
            size="small"
            className="usersSearch"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FiSearch />
                </InputAdornment>
              ),
            }}
          />
          <Chip
            label={`${filtered.length} result(s)`}
            size="small"
            className="usersCount"
          />
        </Box>

        <Box className="usersTableWrap">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className="th">ID</TableCell>
                <TableCell className="th">Title</TableCell>
                <TableCell className="th">City</TableCell>

                <TableCell className="th">Listing</TableCell>
                <TableCell className="th">Visit</TableCell>
                <TableCell className="th" align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="emptyRow">
                    No Location found.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((r, index) => (
                  <TableRow key={r?._id} hover className="tr">
                    <TableCell className="idCell">{index + 1}</TableCell>

                    <TableCell>
                      <Box className="userCell">
                        <Avatar
                          src={`${apis().localUrl + r?.imageUrl}` || undefined}
                          alt={r?.title}
                          className="userAvatar"
                        >
                          {r?.title || "U"}
                        </Avatar>
                        <Box className="userMeta">
                          <Typography className="userName">
                            {r?.title}
                          </Typography>

                          <Typography className="userMini">
                            {r?.imageUrl ? "Has photo" : "No photo"}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <span>{r?.city}</span>
                    </TableCell>

                    <TableCell>
                      <span>{r?.listing?.length}</span>
                    </TableCell>
                    <TableCell>
                      <span className="listingsPill">{r?.visit || 0}</span>
                    </TableCell>

                    <TableCell align="right">
                      <Box className="actions">
                        <MUITooltip title="Edit">
                          <IconButton
                            size="small"
                            className="iconBtn"
                            onClick={() => openEdit(r)}
                          >
                            <FiEdit2 />
                          </IconButton>
                        </MUITooltip>

                        <MUITooltip title="Delete">
                          <IconButton
                            size="small"
                            className="iconBtn danger"
                            onClick={() => removeRow(r?._id)}
                          >
                            <FiTrash2 />
                          </IconButton>
                        </MUITooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      {/* Modal */}
      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setEditing(null);
          setUrls([]);
        }}
        centered
      >
        <Formik
          enableReinitialize
          key={editing?._id || "new"}
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={(values, helpers) => {
            saveUser(values);
            helpers.setSubmitting(false);
          }}
          innerRef={formikRef}
        >
          {(formik) => (
            <form onSubmit={formik.handleSubmit}>
              <Modal.Header closeButton>
                <Modal.Title>
                  {editing ? "Edit Location" : "Add New Location"}
                </Modal.Title>
              </Modal.Header>

              <Modal.Body>
                <div className="modalGrid">
                  <div className="modalField modalField--full">
                    <CustomFileUploader
                      multiple={false}
                      onEdit={urls}
                      onChange={(file) => handleFileChange(file, formik)}
                    />

                    {formik.touched.imageUrl && formik.errors.imageUrl ? (
                      <div className="invalid-feedback d-block">
                        {formik.errors.imageUrl}
                      </div>
                    ) : null}
                  </div>

                  <div className="modalField modalField--full">
                    <label className="modalLabel">Title</label>
                    <input
                      type="text"
                      className={`form-control ${
                        formik.touched.title && formik.errors.title
                          ? "is-invalid"
                          : ""
                      }`}
                      placeholder="Location name"
                      {...formik.getFieldProps("title")}
                    />
                    {formik.touched.title && formik.errors.title ? (
                      <div className="invalid-feedback text-danger">
                        {formik.errors.title}
                      </div>
                    ) : null}
                  </div>
                  <div className="modalField modalField--full">
                    <label className="modalLabel">Title</label>

                    <Autocomplete
                      options={staticData().pakistanMajorCities}
                      value={formik.values.city}
                      onChange={(e, value) =>
                        formik.setFieldValue("city", value)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="city"
                          type="text"
                          onBlur={formik.handleBlur}
                          fullWidth
                          size="small"
                          error={
                            formik.touched.city && Boolean(formik.errors.city)
                          }
                          helperText={formik.touched.city && formik.errors.city}
                        />
                      )}
                    />
                  </div>
                </div>
              </Modal.Body>

              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowModal(false);
                    setEditing(null);
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant="primary"
                  type="submit"
                  disabled={formik.isSubmitting}
                >
                  Save
                </Button>
              </Modal.Footer>
            </form>
          )}
        </Formik>
      </Modal>
    </Box>
  );
}
